import { motion } from "framer-motion";
import { Instagram, MessageCircle, Users, Zap } from "lucide-react";
import { INSTAGRAM_URL, WHATSAPP, scrollToId, useLang } from "@/i18n";

export default function WhatsAppGrid() {
  const { t } = useLang();
  const w = t.whatsapp;

  const cards = [
    { key: "familias", icon: Users, title: w.familias, sub: w.familiasSub, links: WHATSAPP.familias },
    { key: "alumnado", icon: Zap, title: w.alumnado, sub: w.alumnadoSub, links: WHATSAPP.alumnado },
    { key: "general", icon: MessageCircle, title: w.general, sub: w.generalSub, links: WHATSAPP.general },
  ];

  return (
    <section id="comunidad" className="px-5 py-24 sm:px-8 sm:py-32" data-testid="whatsapp-community-section">
      <div className="mx-auto max-w-7xl">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.3em] text-accent"
        >
          {w.label}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-5xl"
        >
          {w.title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-4 max-w-xl text-sm text-zinc-400 sm:text-base"
        >
          {w.subtitle}
        </motion.p>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {cards.map((card, ci) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.key}
                data-testid={`whatsapp-group-${card.key}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: ci * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-card p-6 shadow-2xl shadow-black/80 transition-colors duration-300 hover:border-accent/60 sm:p-7"
              >
                <div className="pointer-events-none absolute -left-16 -bottom-16 h-48 w-48 rounded-full bg-accent/10 blur-[70px]" />
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#25D366]/15 text-[#25D366]">
                    <Icon size={22} />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-extrabold uppercase tracking-tight sm:text-xl">{card.title}</h3>
                    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">{card.sub}</p>
                  </div>
                </div>

                {card.links.length > 0 ? (
                  <div className="mt-6 grid flex-1 grid-cols-1 gap-3 content-start">
                    {card.links.map((g) => (
                      <a
                        key={g.code}
                        data-testid={`whatsapp-join-${card.key}-${g.code.replace(".", "-").toLowerCase()}`}
                        href={g.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-between rounded-lg border border-white/10 bg-panel px-4 py-3.5 transition-all duration-300 hover:border-accent hover:bg-accent/10"
                      >
                        <span className="font-display text-sm font-extrabold uppercase tracking-wide text-white">{g.code}</span>
                        <span className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-400 transition-colors duration-300 group-hover:text-accent">
                          <MessageCircle size={14} /> {w.join}
                        </span>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="mt-6 flex flex-1 flex-col gap-3">
                    <p className="flex-1 text-sm leading-relaxed text-zinc-400">{w.note}</p>
                    <button
                      type="button"
                      data-testid="whatsapp-general-contact"
                      onClick={() => scrollToId("contacto")}
                      className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-accent/70 px-5 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-accent transition-all duration-300 hover:bg-accent hover:text-white"
                    >
                      <Instagram size={14} /> {w.generalCta}
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}